import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Smartphone, AlertTriangle, Info } from 'lucide-react';
import { PageLayout } from '../../../components/ui/PageLayout';
import Input from '../../../components/ui/Input';
import Switch from '../../../components/ui/Switch';
import { Spinner } from '../../../components/ui/Spinner';
import StickyActionBar from '../../../components/ui/StickyActionBar';
import { useToast, ToastRenderer } from '../../../hooks/useToast';
import {
  getAppVersionPolicies,
  updateAppVersionPolicy,
  AppVersionPolicyView,
} from '../../../api/General/AppVersion';

/** Editable mirror of a platform policy row. */
interface PolicyDraft {
  id: number;
  platform: string;
  minVersion: string;
  latestVersion: string;
  forceUpdate: boolean;
  message: string;
  storeUrl: string;
  updatedOn?: string | null;
}

const VERSION_PATTERN = /^\d+(\.\d+){0,3}$/;
const isValidVersion = (v: string) => VERSION_PATTERN.test(v.trim());

const toDraft = (p: AppVersionPolicyView): PolicyDraft => ({
  id: p.id,
  platform: p.platform,
  minVersion: p.minVersion ?? '',
  latestVersion: p.latestVersion ?? '',
  forceUpdate: !!p.forceUpdate,
  message: p.message ?? '',
  storeUrl: p.storeUrl ?? '',
  updatedOn: p.updatedOn,
});

const platformLabel = (platform: string) => (platform === 'ios' ? 'iOS' : 'Android');

const sameRow = (a: PolicyDraft, b: PolicyDraft) =>
  a.minVersion.trim() === b.minVersion.trim() &&
  a.latestVersion.trim() === b.latestVersion.trim() &&
  a.forceUpdate === b.forceUpdate &&
  a.message.trim() === b.message.trim() &&
  a.storeUrl.trim() === b.storeUrl.trim();

export default function AppVersionIndex() {
  const { t } = useTranslation();
  const { toast, showToast, dismissToast } = useToast();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [original, setOriginal] = useState<PolicyDraft[]>([]);
  const [drafts, setDrafts] = useState<PolicyDraft[]>([]);

  const fetchPolicies = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getAppVersionPolicies();
      if (res.errors?.length) {
        showToast(res.errors[0]?.message || t('Failed to load app version policies'), 'error');
      } else {
        // Stable order: Android first, then iOS.
        const rows = (res.payload ?? [])
          .map(toDraft)
          .sort((a, b) => a.platform.localeCompare(b.platform));
        setOriginal(rows);
        setDrafts(rows.map(r => ({ ...r })));
      }
    } catch (e) {
      console.error(e);
      showToast(t('Failed to load app version policies'), 'error');
    } finally {
      setLoading(false);
    }
  }, [t, showToast]);

  useEffect(() => {
    fetchPolicies();
  }, [fetchPolicies]);

  const patchDraft = (platform: string, patch: Partial<PolicyDraft>) =>
    setDrafts(rows => rows.map(r => (r.platform === platform ? { ...r, ...patch } : r)));

  const hasUnsavedChanges = useMemo(
    () =>
      drafts.some(d => {
        const orig = original.find(o => o.platform === d.platform);
        return !orig || !sameRow(d, orig);
      }),
    [drafts, original],
  );

  const invalidRows = useMemo(
    () => drafts.filter(d => !isValidVersion(d.minVersion) || !isValidVersion(d.latestVersion)),
    [drafts],
  );

  const handleReset = () => setDrafts(original.map(r => ({ ...r })));

  const handleSave = async () => {
    if (invalidRows.length > 0) {
      showToast(t('Versions must be numeric, e.g. 1.0.28'), 'error');
      return;
    }
    const changed = drafts.filter(d => {
      const orig = original.find(o => o.platform === d.platform);
      return !orig || !sameRow(d, orig);
    });
    if (changed.length === 0) return;

    setSaving(true);
    try {
      for (const row of changed) {
        const res = await updateAppVersionPolicy({
          platform: row.platform,
          minVersion: row.minVersion.trim(),
          latestVersion: row.latestVersion.trim(),
          forceUpdate: row.forceUpdate,
          message: row.message.trim() || null,
          storeUrl: row.storeUrl.trim() || null,
        });
        if (res.errors?.length) {
          showToast(res.errors[0]?.message || t('Failed to save changes'), 'error');
          setSaving(false);
          return;
        }
      }
      showToast(t('App version policy saved'), 'success');
      await fetchPolicies();
    } catch (e) {
      console.error(e);
      showToast(t('Failed to save changes'), 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <PageLayout
      title={t('Mobile App Version')}
      description={t('Control the minimum and latest supported versions of the mobile app, and force updates when needed.')}
      icon={Smartphone}
      accentColor="slate"
      maxWidth="5xl"
    >
      <div className="flex flex-col gap-6">
        {/* How it works */}
        <div className="flex items-start gap-3 rounded-xl border border-border bg-muted/30 p-4">
          <Info className="w-5 h-5 shrink-0 text-muted-foreground mt-0.5" />
          <p className="text-sm text-muted-foreground">
            {t('These settings apply platform-wide to every company. The mobile app checks them on launch and when resumed: users on a version below the minimum are prompted to update, and blocked entirely when "Force update" is on. Leave the minimum at 0.0.0 to disable the check.')}
          </p>
        </div>

        {loading ? (
          <div className="flex justify-center py-16">
            <Spinner size="lg" />
          </div>
        ) : drafts.length === 0 ? (
          <div className="rounded-xl border border-border bg-card p-10 text-center text-sm text-muted-foreground">
            {t('No app version policies found.')}
          </div>
        ) : (
          <>
            {drafts.map(row => {
              const minInvalid = !isValidVersion(row.minVersion);
              const latestInvalid = !isValidVersion(row.latestVersion);
              return (
                <section
                  key={row.platform}
                  className="flex flex-col gap-5 rounded-xl border border-border bg-card p-5 sm:p-6"
                >
                  {/* Platform header */}
                  <div className="flex items-center gap-3">
                    <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-muted text-foreground">
                      <Smartphone size={18} />
                    </div>
                    <div className="min-w-0">
                      <h3 className="text-sm font-semibold text-foreground">{platformLabel(row.platform)}</h3>
                      {row.updatedOn && (
                        <p className="text-xs text-muted-foreground">
                          {t('Last updated')}: {new Date(row.updatedOn).toLocaleString()}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Versions */}
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div className="flex flex-col gap-1.5">
                      <label className="text-sm font-medium text-foreground">{t('Minimum supported version')}</label>
                      <Input
                        value={row.minVersion}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          patchDraft(row.platform, { minVersion: e.target.value })
                        }
                        placeholder="1.0.28"
                      />
                      {minInvalid ? (
                        <span className="text-xs text-rose-600 dark:text-rose-400">
                          {t('Use a numeric version, e.g. 1.0.28')}
                        </span>
                      ) : (
                        <span className="text-xs text-muted-foreground">
                          {t('Apps below this version are prompted (or blocked) to update.')}
                        </span>
                      )}
                    </div>
                    <div className="flex flex-col gap-1.5">
                      <label className="text-sm font-medium text-foreground">{t('Latest available version')}</label>
                      <Input
                        value={row.latestVersion}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          patchDraft(row.platform, { latestVersion: e.target.value })
                        }
                        placeholder="1.0.30"
                      />
                      {latestInvalid ? (
                        <span className="text-xs text-rose-600 dark:text-rose-400">
                          {t('Use a numeric version, e.g. 1.0.28')}
                        </span>
                      ) : (
                        <span className="text-xs text-muted-foreground">
                          {t('Shown to users as the newest version (informational).')}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Store URL */}
                  <div className="flex flex-col gap-1.5">
                    <label className="text-sm font-medium text-foreground">{t('Store URL')}</label>
                    <Input
                      value={row.storeUrl}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        patchDraft(row.platform, { storeUrl: e.target.value })
                      }
                      placeholder={
                        row.platform === 'ios'
                          ? 'https://apps.apple.com/app/id...'
                          : 'https://play.google.com/store/apps/details?id=com.mowazaf.app'
                      }
                    />
                    <span className="text-xs text-muted-foreground">
                      {t('Where the "Update now" button sends the user.')}
                    </span>
                  </div>

                  {/* Message */}
                  <div className="flex flex-col gap-1.5">
                    <label className="text-sm font-medium text-foreground">{t('Update message (optional)')}</label>
                    <textarea
                      value={row.message}
                      onChange={e => patchDraft(row.platform, { message: e.target.value })}
                      placeholder={t('e.g. A new version is available with important fixes.')}
                      rows={2}
                      className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
                    />
                  </div>

                  {/* Force update */}
                  <div className="flex flex-col gap-3 rounded-lg border border-border bg-muted/30 p-4">
                    <Switch
                      checked={row.forceUpdate}
                      onCheckedChange={(checked: boolean) => patchDraft(row.platform, { forceUpdate: checked })}
                      label={t('Force update')}
                      description={t('Block users below the minimum version with a screen they cannot dismiss.')}
                    />
                    {row.forceUpdate && isValidVersion(row.minVersion) && (
                      <div className="flex items-start gap-2 text-xs text-amber-700 dark:text-amber-400">
                        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                        <span>
                          {t('Users on a version below {{version}} will be locked out until they update.', {
                            version: row.minVersion.trim(),
                          })}
                        </span>
                      </div>
                    )}
                  </div>
                </section>
              );
            })}

            <StickyActionBar
              hasUnsavedChanges={hasUnsavedChanges}
              isSaving={saving}
              onReset={handleReset}
              onSave={handleSave}
              t={t}
            />
          </>
        )}

        <ToastRenderer toast={toast} onDismiss={dismissToast} />
      </div>
    </PageLayout>
  );
}
